import { useState } from 'react';
import { absolutize, ENDPOINTS, parseYouTube } from '../lib/api.js';
import { useCmsList } from '../hooks/useCmsList.js';
import { useVideoCrop } from '../hooks/useVideoCrop.js';
import { TESTIMONIAL_FALLBACK } from '../content/home.js';
import Lightbox from '../components/ui/Lightbox.jsx';
import Reveal from '../components/ui/Reveal.jsx';
import AccentedHeading from '../components/ui/Accent.jsx';

export default function Testimonials() {
  const { items } = useCmsList(ENDPOINTS.testimonials, {
    fallback: [TESTIMONIAL_FALLBACK],
    map: (t) => ({
      id: t.id ?? t.name,
      quote: t.testimonial ?? t.quote,
      name: t.name,
      farm: t.farm ?? t.role,
      location: t.location,
      image: absolutize(t.image),
      birdCount: t.bird_count,
      experience: t.experience,
      video: parseYouTube(t.video_url),
    }),
  });

  if (!items.length) return null;

  const [featured, ...rest] = items;

  return (
    <section id="customers" className="scroll-mt-24 bg-wash py-16 lg:py-20">
      <div className="wrap">
        <div className="mb-10 text-center">
          <h2 className="font-display text-[24px] font-bold text-deep lg:text-[30px]">
            <AccentedHeading
              text="Farmers Like You Already Made The Switch"
              accent="Like You"
              on="light"
            />
          </h2>
        </div>

        <Reveal>
          <Featured t={featured} />
        </Reveal>

        {/* Additional testimonials sit below the featured one, so a single
            review still reads as a deliberate feature rather than a thin list. */}
        {rest.length > 0 && (
          <ul className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((t, i) => (
              <Reveal as="li" key={t.id} delay={Math.min(i, 4) * 80}>
                <Compact t={t} />
              </Reveal>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function Featured({ t }) {
  // Detected once here and passed down, so the column width and the card
  // agree on the shape of the clip rather than each working it out.
  const aspect = useVideoCrop(t.video?.posterUrl, t.video?.posterFallbackUrl);

  // An upright clip only needs a narrow column; a landscape one earns half
  // the card. Without this an upright video sat in a column twice its width.
  const columns = aspect < 1 ? 'lg:grid-cols-[360px_1fr]' : 'lg:grid-cols-2';

  return (
    <figure className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className={t.video ? `grid ${columns}` : ''}>
        {t.video && <VideoPlayer video={t.video} name={t.name} aspect={aspect} />}

        <div className="flex flex-col justify-center p-6 sm:p-9">
          <span
            aria-hidden="true"
            className="mb-2 font-display text-[42px] leading-none text-amber"
          >
            &ldquo;
          </span>

          <blockquote className="mb-6 text-[15px] leading-relaxed text-ink-soft lg:text-[16px]">
            {t.quote}
          </blockquote>

          <Attribution t={t} />

          {/* Scale and tenure are the two things another farm owner weighs
              first, so they are surfaced rather than left in the database. */}
          {(t.birdCount || t.experience) && (
            <dl className="mt-5 flex flex-wrap gap-x-8 gap-y-3 border-t border-line pt-5">
              {t.birdCount && (
                <div>
                  <dt className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-muted">
                    Flock size
                  </dt>
                  <dd className="font-display text-[18px] font-bold text-field">{t.birdCount}</dd>
                </div>
              )}
              {t.experience && (
                <div>
                  <dt className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-muted">
                    Years farming
                  </dt>
                  <dd className="font-display text-[18px] font-bold text-field">{t.experience}</dd>
                </div>
              )}
            </dl>
          )}
        </div>
      </div>
    </figure>
  );
}

function Compact({ t }) {
  return (
    <figure className="flex h-full flex-col rounded-2xl border border-line bg-white p-5">
      <blockquote className="mb-4 flex-1 text-[14px] leading-relaxed text-ink-soft">
        &ldquo;{t.quote}&rdquo;
      </blockquote>
      <Attribution t={t} small />
    </figure>
  );
}

function Attribution({ t, small = false }) {
  const size = small ? 34 : 44;

  return (
    <figcaption className="flex items-center gap-3">
      {t.image ? (
        <img
          src={t.image}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          className="flex-none rounded-full object-cover"
          style={{ width: size, height: size }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : (
        <span
          className="flex flex-none items-center justify-center rounded-full bg-field font-display font-bold text-white"
          style={{ width: size, height: size }}
          aria-hidden="true"
        >
          {t.name?.charAt(0)}
        </span>
      )}
      <span>
        <b className={`block text-deep ${small ? 'text-[13px]' : 'text-[14px]'}`}>{t.name}</b>
        <span className={`text-muted ${small ? 'text-[11.5px]' : 'text-[12.5px]'}`}>
          {[t.farm, t.location].filter(Boolean).join(' · ')}
        </span>
      </span>
    </figcaption>
  );
}

/**
 * Poster card, click-to-play into a lightbox.
 *
 * Two problems are being solved here.
 *
 * First, load cost: a YouTube iframe pulls roughly half a megabyte before
 * anyone presses play, which is a poor trade on Kenyan and Ugandan mobile
 * data. So the card is a plain image until the visitor actually asks for it.
 *
 * Second, shape. Testimonials are filmed however the farmer held the phone.
 * The card takes the detected shape of the picture, so an upright clip gets an
 * upright card and a landscape clip gets a wide one, automatically. But the
 * player itself always runs 16:9 in a lightbox over a dark backdrop, because
 * that is the shape YouTube actually serves — squeezing a 16:9 stream into an
 * upright box is what made the video play postage-stamp small. Large and
 * centred, any bars in the source fall away against the dark backdrop.
 */
function VideoPlayer({ video, name, aspect }) {
  const [open, setOpen] = useState(false);
  const portrait = aspect < 1;

  return (
    <div className="relative flex items-center justify-center overflow-hidden bg-deep p-5 lg:p-6">
      {/* Blurred, over-scaled copy of the frame, so the space around an
          upright card carries colour rather than sitting as a flat slab. */}
      <img
        src={video.posterUrl}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 h-full w-full scale-150 object-cover opacity-30 blur-2xl saturate-150"
      />
      <span aria-hidden="true" className="absolute inset-0 bg-deep/55" />

      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Play video: ${name} on using Chickrah`}
        className={`group relative w-full overflow-hidden rounded-xl shadow-float ${
          portrait ? 'max-w-[300px]' : ''
        }`}
        style={{ aspectRatio: String(aspect) }}
      >
        {/* The picture sits centred inside the thumbnail, so object-cover on a
            box of the detected shape lands exactly on it and trims the bars. */}
        <img
          src={video.posterUrl}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
          onError={(e) => {
            // maxresdefault does not exist for every upload; hqdefault always does.
            if (e.currentTarget.src !== video.posterFallbackUrl) {
              e.currentTarget.src = video.posterFallbackUrl;
            }
          }}
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-t from-deep/80 via-transparent to-transparent"
        />
        <span
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-amber shadow-float transition duration-300 group-hover:scale-110"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="#122E1B" className="ml-1">
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
        <span className="absolute bottom-3 left-3 right-3 text-left font-mono text-[11px] uppercase tracking-[0.12em] text-white/90">
          Watch {name?.split(' ')[0]}&rsquo;s story
        </span>
      </button>

      {open && (
        <Lightbox
          kind="video"
          src={video.embedUrl}
          title={`${name} on using Chickrah`}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
